/**
 * Report a render-time crash to the server, from a React tree that has already
 * failed.
 *
 * Every constraint here comes from WHO calls it: `ErrorBoundary.componentDidCatch`,
 * with the app in an unknown state.
 *
 * - **It cannot throw.** Every path is inside a try/catch, and the promise
 *   resolves to `null` rather than rejecting. A reporter that throws from a
 *   catch handler takes down the fallback screen too, and the user gets a blank
 *   page instead of a message.
 * - **It cannot depend on React state or context.** The boundary is mounted
 *   twice — once above `AuthProvider` — so the identity comes off the token in
 *   storage and the tenant/user attribution is done server-side from that token.
 * - **It is bounded.** `fetchWithDeadline`, not a bare `fetch`: a connection the
 *   server accepts and then stalls on would otherwise leave the promise pending
 *   forever and the error screen without its reference (the class
 *   `tools/check_unbounded_fetch.py` exists for).
 * - **It is deduplicated per session.** A crash loop reports each distinct
 *   fingerprint ONCE and replays the cached id for every repeat, so reloading
 *   into the same crash cannot flood the backend — and shows the user the same
 *   reference each time, which is what they are about to quote.
 *
 * Module-level state, deliberately: two boundary instances and any number of
 * remounts share one budget, which is the only way the cap means anything.
 */
import { API_BASE_URL } from '../api/config';
import { fetchWithDeadline } from '../api/fetchDeadline';
import { AUTH_STORAGE_KEYS } from '../auth/storageKeys';
import { redactClientText } from './redactClientText';

const REPORT_PATH = '/api/v1/monitoring/client-errors';

/**
 * Distinct crash classes reported per browsing session. A page that manages to
 * produce six unrelated render crashes is broken in a way one more report will
 * not clarify.
 */
export const MAX_DISTINCT_REPORTS_PER_SESSION = 5;

/** Deadline for the report itself. Shorter than the app default: the user is */
/** staring at an error screen waiting for the reference. */
const REPORT_TIMEOUT_MS = 8_000;

const STACK_MAX = 8_000;
const MESSAGE_MAX = 2_000;

/** fingerprint -> resolved errorId (or null when the report did not land). */
const reported = new Map<string, string | null>();
/** fingerprint -> in-flight request, so a double crash makes ONE request. */
const inFlight = new Map<string, Promise<string | null>>();

export interface CrashReport {
  error: unknown;
  componentStack?: string | null;
}

/** Test seam: forget this session's reports. Never called by product code. */
export function __resetCrashReporterForTests(): void {
  reported.clear();
  inFlight.clear();
}

/** How many distinct crash classes have been reported so far this session. */
export function reportedCrashCount(): number {
  return reported.size;
}

/**
 * The frontend build the running bundle came from. `VITE_BUILD_VERSION` is the
 * deploy's commit sha in a built bundle and `local` in development.
 */
function buildVersion(): string {
  return import.meta.env.VITE_BUILD_VERSION || 'local';
}

/**
 * The content-hashed module entry this document booted from, e.g.
 * `/assets/index-B3tBRw3X.js`.
 *
 * Vite re-hashes it on every build, which makes it the one field that separates
 * two sessions running DIFFERENT bundles inside a single deploy window — the
 * correlation WI ad05f876 had to do by hand against a GitHub Actions log,
 * because the backend deploys up to ~12 minutes before the frontend and its
 * version header says nothing about the bundle in front of the user. Same
 * reading `VersionChecker` uses to decide whether a new bundle has published;
 * Vite's own `/@vite/client` is skipped because its path never changes.
 */
function bundleEntry(): string | null {
  try {
    const scripts = document.querySelectorAll<HTMLScriptElement>(
      'script[type="module"][src]'
    );
    for (const script of Array.from(scripts)) {
      const src = script.getAttribute('src');
      if (src && !src.startsWith('/@')) return src;
    }
  } catch {
    // A document without a queryable head is not worth a second failure here.
  }
  return null;
}

/**
 * A stable identity for one crash class, computed client-side so a repeat can be
 * suppressed before it becomes a request.
 *
 * Built from the error name, a NORMALIZED message and the nearest component —
 * never the JavaScript stack, which in a production bundle is minified and
 * content-hashed and would mint a fresh identity on every deploy. The server
 * computes its own fingerprint the same way; the two do not have to agree,
 * because this one only has to be stable within a session.
 */
function fingerprint(name: string, message: string, componentStack: string | null): string {
  const normalized = message
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<uuid>')
    .replace(/https?:\/\/[^\s'")]+/g, '<url>')
    .replace(/\b\d+\b/g, '<n>')
    .replace(/\s+/g, ' ')
    .trim();
  const component = componentStack?.match(/^\s*(?:at|in)\s+([A-Za-z0-9_$.]+)/m)?.[1] ?? '';
  return `${name}|${normalized}|${component}`;
}

/**
 * The access token as STORED, with no refresh attempt.
 *
 * Deliberately not `getValidAccessToken()`: that refreshes an expiring token and,
 * on failure, calls `handleSessionExpired()` — wiping the session and firing
 * SESSION_EXPIRED. Doing that from a crash handler would turn a render error
 * into a forced sign-out. An expired token here simply means the report 401s and
 * the screen falls back to its timestamp-only reference.
 */
function storedAccessToken(): string | null {
  try {
    return (
      localStorage.getItem(AUTH_STORAGE_KEYS.emulationToken) ||
      localStorage.getItem(AUTH_STORAGE_KEYS.accessToken)
    );
  } catch {
    return null;
  }
}

async function postReport(body: Record<string, unknown>): Promise<string | null> {
  const token = storedAccessToken();
  if (!token) return null;

  const response = await fetchWithDeadline(
    `${API_BASE_URL}${REPORT_PATH}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    },
    REPORT_TIMEOUT_MS
  );
  if (!response.ok) return null;
  const payload = (await response.json()) as { error_id?: string | null };
  return payload.error_id ?? null;
}

/**
 * Record a render crash and resolve to the errorId the screen should quote, or
 * `null` when no record was made (offline, signed out, over the session cap, or
 * the server declined).
 *
 * Never rejects.
 */
export async function reportClientCrash({
  error,
  componentStack,
}: CrashReport): Promise<string | null> {
  try {
    const err = error as { name?: string; message?: string; stack?: string } | null;
    const name = (typeof err?.name === 'string' && err.name) || 'Error';
    const message =
      (typeof err?.message === 'string' && err.message) || String(error ?? 'Unknown error');
    const stack = typeof err?.stack === 'string' ? err.stack : null;
    const stackTrace = componentStack ?? null;

    const key = fingerprint(name, message, stackTrace);
    if (reported.has(key)) return reported.get(key) ?? null;
    const pending = inFlight.get(key);
    if (pending) return pending;

    // The cap counts DISTINCT classes, so a crash loop (one class, many crashes)
    // is bounded by the dedup above and never consumes it.
    if (reported.size >= MAX_DISTINCT_REPORTS_PER_SESSION) return null;

    const body = {
      message: (redactClientText(message) ?? 'Unknown error').slice(0, MESSAGE_MAX),
      name,
      stack: redactClientText(stack)?.slice(0, STACK_MAX) ?? null,
      component_stack: redactClientText(stackTrace)?.slice(0, STACK_MAX) ?? null,
      // Path only — never `search` or `hash`, which are the likeliest place for a
      // search term or a token to ride into a persistent operator-visible store.
      route: window.location.pathname,
      build_version: buildVersion(),
      bundle_entry: bundleEntry(),
      user_agent: navigator.userAgent?.slice(0, 500) ?? null,
      occurred_at: new Date().toISOString(),
    };

    const request = postReport(body)
      .catch(() => null)
      .then((errorId) => {
        reported.set(key, errorId);
        inFlight.delete(key);
        return errorId;
      });
    inFlight.set(key, request);
    return request;
  } catch {
    // Composing the report failed. There is nothing left to report it TO, and
    // the caller is a crash handler — swallow and let the screen fall back.
    return null;
  }
}
